/**
 * Transaction Result Page
 * Display payment transaction result after Cybersource redirect
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTransactionResult } from '@/api/services/paymentService';
import {
  LoadingSpinner,
  ErrorMessage,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@/components/common';
import { ROUTE_PATHS } from '@/utils/constants';
import { formatCurrency } from '@/utils/formatters';
import type { TransactionResultResponse } from '@/types/api.types';

export default function TransactionResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [result, setResult] = useState<TransactionResultResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchResult = async () => {
      if (!token) {
        setError('No transaction token provided');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getTransactionResult(token, controller.signal);
        setResult(data);
      } catch (err) {
        if (controller.signal.aborted) return;

        console.error('Failed to fetch transaction result:', err);
        setError('Failed to load transaction result. Please contact support.');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchResult();

    return () => controller.abort();
  }, [token]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <LoadingSpinner size="lg" message="Loading transaction result..." />
      </div>
    );
  }

  // Error state
  if (error || !result) {
    return (
      <div className="max-w-2xl mx-auto">
        <ErrorMessage
          message={error || 'Unable to load transaction result'}
          variant="card"
        />
        <div className="mt-4">
          <Button variant="ghost" onClick={() => navigate(ROUTE_PATHS.LOGIN)}>
            &larr; Return to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Success State */}
      {result.success ? (
        <>
          {/* Success Header */}
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success/10 mb-4">
              <svg
                className="h-8 w-8 text-success"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-success">Payment Successful</h1>
            <p className="text-sm text-text-muted mt-2">
              Your payment has been processed successfully.
            </p>
          </div>

          {/* Payment Amount Highlight */}
          <Card className="text-center">
            <CardBody className="py-6">
              <p className="text-sm text-text-muted mb-1">Amount Paid</p>
              <p className="text-3xl font-bold text-success">{formatCurrency(result.amount)}</p>
            </CardBody>
          </Card>

          {/* Transaction Details */}
          <Card>
            <CardHeader>
              <CardTitle>Transaction Details</CardTitle>
            </CardHeader>
            <CardBody className="!p-0">
              <table className="w-full">
                <thead className="sr-only">
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3 text-sm text-text-muted">Transaction ID</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right font-mono">
                      {result.transactionId}
                    </td>
                  </tr>
                  <tr className="border-b border-border bg-surface/50">
                    <td className="px-4 py-3 text-sm text-text-muted">Policy Number</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right">
                      {result.policyNumber}
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3 text-sm text-text-muted">Card</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right">
                      **** **** **** {result.cardLast4}
                    </td>
                  </tr>
                  <tr className="border-b border-border bg-surface/50">
                    <td className="px-4 py-3 text-sm text-text-muted">Billing Name</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right">
                      {result.billingName}
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3 text-sm text-text-muted">Email</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right">
                      {result.billingEmail}
                    </td>
                  </tr>
                  <tr className="bg-surface/50">
                    <td className="px-4 py-3 text-sm text-text-muted">Date</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right">
                      {new Date(result.transactionDate).toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>

          {/* Confirmation Email Notice */}
          <Card className="bg-primary/5 border-l-4 border-l-primary">
            <CardBody>
              <p className="text-sm text-text">
                A confirmation email has been sent to{' '}
                <span className="font-semibold">{result.billingEmail}</span>
              </p>
            </CardBody>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <Button variant="ghost" onClick={() => window.print()}>
              Print Receipt
            </Button>
            <Button variant="primary" onClick={() => navigate(ROUTE_PATHS.LOGIN)}>
              Return to Home
            </Button>
          </div>
        </>
      ) : (
        /* Failed State */
        <>
          {/* Failure Header */}
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-danger/10 mb-4">
              <svg
                className="h-8 w-8 text-danger"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-danger">Payment Unsuccessful</h1>
            <p className="text-sm text-text-muted mt-2">
              We were unable to process your payment.
            </p>
          </div>

          {/* Error Details */}
          <Card variant="error">
            <CardBody>
              <div className="flex items-start gap-3">
                <svg
                  className="h-5 w-5 text-danger flex-shrink-0 mt-0.5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-text">Reason</p>
                  <p className="text-sm text-text-muted mt-1">{result.reasonDescription}</p>
                  <p className="text-xs text-text-muted mt-2">
                    Reference: {result.transactionId}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Transaction Details */}
          <Card>
            <CardHeader>
              <CardTitle>Transaction Details</CardTitle>
            </CardHeader>
            <CardBody className="!p-0">
              <table className="w-full">
                <thead className="sr-only">
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="px-4 py-3 text-sm text-text-muted">Transaction ID</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right font-mono">
                      {result.transactionId}
                    </td>
                  </tr>
                  <tr className="border-b border-border bg-surface/50">
                    <td className="px-4 py-3 text-sm text-text-muted">Attempted Amount</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right">
                      {formatCurrency(result.amount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-text-muted">Date</td>
                    <td className="px-4 py-3 text-sm font-medium text-text text-right">
                      {new Date(result.transactionDate).toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>

          {/* Help Information */}
          <Card className="bg-surface">
            <CardBody>
              <p className="font-medium text-text text-sm mb-2">What to do next</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-text-muted">
                <li>Verify your payment information and try again</li>
                <li>Contact your bank if the issue persists</li>
                <li>Call us for assistance if you continue to have trouble</li>
              </ul>
            </CardBody>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <Button variant="ghost" onClick={() => navigate(ROUTE_PATHS.LOGIN)}>
              Return to Home
            </Button>
            <Button variant="primary" onClick={() => navigate(ROUTE_PATHS.CONFIRMATION)}>
              Try Again
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
